import { useEffect, useState } from 'react';
import { AlertTriangle, ArrowUpRight } from 'lucide-react';

import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const META_BUSINESS_SETTINGS_URL = 'https://business.facebook.com/settings';

// Disabling stops the sync and flips the channel flag on every stock row, but
// nothing is sent to the channel itself — the Meta consumer drops a shop whose
// channel row is already disabled before it ever archives anything, and the
// flow never revokes access on Meta's side. The copy spells out exactly that.
function Consequences({ channel }) {
	if (channel.id === 'meta-commerce') {
		return (
			<ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
				<li>We stop sending product updates to your Meta catalogue, so it is no longer kept up to date.</li>
				<li>Products already in the catalogue stay there as they are — nothing is archived or removed on Meta's side.</li>
				<li>Any Meta Commerce add-on comes off your plan.</li>
				<li>
					Your Meta catalogue, the NearSt system user and any Meta Shop built on the catalogue stay on Meta's side. To disconnect
					fully, remove the catalogue and system user in{' '}
					<a
						href={META_BUSINESS_SETTINGS_URL}
						target="_blank"
						rel="noopener noreferrer"
						className="inline-flex items-center gap-0.5 font-medium text-foreground underline underline-offset-4"
					>
						Meta Business Settings <ArrowUpRight className="h-3 w-3" />
					</a>
					, and take down or re-point the Shop in Commerce Manager.
				</li>
			</ul>
		);
	}
	return (
		<ul className="list-disc space-y-2 pl-5 text-sm text-muted-foreground">
			<li>We stop sending product updates to {channel.title}, so anything already there is no longer kept up to date.</li>
			<li>Any {channel.title} add-on comes off your plan.</li>
		</ul>
	);
}

export function DisableChannelDialog({ channel, open, onOpenChange, onConfirm }) {
	const [pending, setPending] = useState(false);
	const [error, setError] = useState(null);

	useEffect(() => {
		if (!open) setError(null);
	}, [open]);

	const close = () => {
		if (!pending) onOpenChange(false);
	};

	const confirm = async () => {
		setPending(true);
		setError(null);
		try {
			await onConfirm();
		} catch (err) {
			setError(err);
		} finally {
			setPending(false);
		}
	};

	return (
		<Dialog open={open} onOpenChange={(next) => !next && close()}>
			<DialogContent className="max-w-lg">
				<DialogHeader>
					<DialogTitle className="text-xl font-extrabold tracking-tight">Disable {channel.title}?</DialogTitle>
					<DialogDescription>This takes effect straight away. Here's what happens:</DialogDescription>
				</DialogHeader>

				<Consequences channel={channel} />

				{error ? (
					<div role="alert" className="flex items-start gap-2.5 rounded-xl border border-destructive/30 bg-destructive/10 p-3">
						<AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-destructive" />
						<p className="text-xs font-medium text-destructive">
							We couldn't disable {channel.title}, so nothing has changed. Please try again, or contact support if it keeps
							happening.
						</p>
					</div>
				) : null}

				<DialogFooter className="!flex-col-reverse gap-2 pt-2 sm:!flex-row sm:justify-end">
					<button
						type="button"
						onClick={close}
						disabled={pending}
						className="rounded-xl px-4 py-2.5 text-sm font-semibold text-muted-foreground transition hover:text-foreground disabled:opacity-50"
					>
						Keep {channel.title}
					</button>
					<button
						type="button"
						onClick={confirm}
						disabled={pending}
						className="whitespace-nowrap rounded-xl bg-destructive px-5 py-2.5 text-sm font-semibold text-destructive-foreground transition hover:bg-destructive/90 disabled:opacity-50"
					>
						{pending ? 'Disabling…' : `Disable ${channel.title}`}
					</button>
				</DialogFooter>
			</DialogContent>
		</Dialog>
	);
}
